// Remote config (roadmap §4.3): the API serves a versioned JSON doc; tunables and flags in it
// override the built-in defaults. Applied once at boot, before the engine is created.

import { TUNABLES } from "./config/tunables";
import { setFlags } from "./flags";

export interface RemoteConfig {
  version: number;
  tunables?: Record<string, unknown>; // deep-partial of TUNABLES
  flags?: Record<string, boolean>;
  season?: { id: number; name: string; theme?: SeasonTheme; endsAt: string } | null;
}

/** Season themes (§5.2): market regime weights, special events, NPC flavor. */
export interface SeasonTheme {
  title?: string;
  marketFates?: Record<string, number>; // overrides TUNABLES.market.fates
  hackathonEveryDays?: number;
  banner?: string;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Deep-merge `over` into `target`, only for keys that already exist with the same type. */
export function deepMerge(target: Record<string, unknown>, over: Record<string, unknown>, path = ""): string[] {
  const applied: string[] = [];
  for (const [k, v] of Object.entries(over)) {
    if (!(k in target)) continue; // unknown key: ignore (old client, new config)
    const cur = target[k];
    if (isObj(cur) && isObj(v)) applied.push(...deepMerge(cur, v, `${path}${k}.`));
    else if (typeof cur === typeof v || (Array.isArray(cur) && Array.isArray(v))) {
      target[k] = v;
      applied.push(`${path}${k}`);
    }
  }
  return applied;
}

/** Apply a config doc to the live TUNABLES + flags. Returns the keys that changed. */
export function applyRemoteConfig(cfg: RemoteConfig): string[] {
  const applied: string[] = [];
  if (cfg.tunables) applied.push(...deepMerge(TUNABLES as unknown as Record<string, unknown>, cfg.tunables));
  const theme = cfg.season?.theme;
  if (theme?.marketFates) applied.push(...deepMerge(TUNABLES.market.fates as unknown as Record<string, unknown>, theme.marketFates, "market.fates."));
  if (theme?.hackathonEveryDays) applied.push(...deepMerge(TUNABLES.hackathons as unknown as Record<string, unknown>, { everyDays: theme.hackathonEveryDays }, "hackathons."));
  if (cfg.flags) setFlags(cfg.flags);
  return applied;
}
